import React from "react";
import "./RegisterWindow.css";
import LanguageString from "./LanguageString";
import { ModalDismissForm, ModalWrapper } from "./ModalWindow";
import LoadingIcon from "./LoadingIcon";
import GameLogo from "../Resources/logo.png";
import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import AnimatedCheckmark from "./AnimatedCheckmark";
import { useSendCooldown } from "./useSendCooldown";

// 인증 메일 발송 정책 안내 문구 (서버: IP/이메일당 30분에 1회)
const MAIL_LIMIT_NOTICE = "※ 인증 메일은 30분에 한 번만 전송할 수 있습니다.";

type RegisterWindowProps = {
  handleDismiss: () => void;
  onError: (err: string | ErrorObject) => void;
  openLoginWindow: () => void; // 추가된 prop
};

// 회원가입 단계: 정보 입력 -> 이메일 인증번호 입력
type RegisterStep = "form" | "code";

export default function RegisterWindow({ handleDismiss, onError, openLoginWindow }: RegisterWindowProps) {
  const [step, setStep] = React.useState<RegisterStep>("form");
  const [loggingIn, setLoggingIn] = React.useState(false);
  const [passwordMismatch, setPasswordMismatch] = React.useState(false); // 비밀번호 불일치 상태
  const [invalidEmail, setInvalidEmail] = React.useState(false); // 이메일 유효성 상태
  const [invalidUsername, setInvalidUsername] = React.useState(false); // 아이디 유효성 상태
  const [invalidPassword, setInvalidPassword] = React.useState(false); // 비밀번호 유효성 상태
  const [registerCompleted, setRegisterCompleted] = React.useState<boolean>(false);

  // 이메일 인증 관련 상태
  const [code, setCode] = React.useState<string>("");
  const [timer, setTimer] = React.useState<number | null>(null);

  // 1단계에서 입력한 값을 2단계까지 유지하기 위한 상태
  const [account, setAccount] = React.useState({ username: "", email: "", password: "", password2: "" });

  const abortController = React.useRef(new AbortController());
  const usernameInput = React.useRef<HTMLInputElement>(null);
  const emailInput = React.useRef<HTMLInputElement>(null);
  const passwordInput = React.useRef<HTMLInputElement>(null);
  const passwordInput2 = React.useRef<HTMLInputElement>(null);
  const turnstileInput = React.useRef<TurnstileInstance>(null);
  const submitButton = React.useRef<HTMLButtonElement>(null);
  const timerIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // 전송 중 중복 클릭을 막는 즉시 가드 (state 갱신 지연 대비)
  const inFlightRef = React.useRef(false);
  // 전송 후 재클릭 쿨다운 (localStorage 저장 → 창을 닫았다 열어도 유지)
  const { remaining: cooldown, start: startCooldown, check: checkCooldown } = useSendCooldown();

  React.useEffect(() => {
    usernameInput.current?.focus();
  }, []);

  // 컴포넌트 언마운트 시 타이머 정리
  React.useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  // 유효성 검사 함수들
  function validateEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  // 가입 허용 이메일 도메인 — 서버(teraController.php ALLOWED_EMAIL_DOMAINS)와 항상 동일하게 유지.
  // 휴대폰 인증 등으로 대량 생성이 어려운 정식 대형 제공사만 허용 (임시메일/무제한 생성 제공사 차단).
  const ALLOWED_EMAIL_DOMAINS = [
    "naver.com",
    "gmail.com",
    "daum.net",
    "hanmail.net",
    "kakao.com",
    "nate.com",
    "outlook.com",
    "hotmail.com",
    "live.com",
    "icloud.com",
    "me.com",
  ];
  function isAllowedEmailDomain(email: string): boolean {
    const at = email.lastIndexOf("@");
    if (at === -1) return false;
    return ALLOWED_EMAIL_DOMAINS.includes(email.slice(at + 1).toLowerCase());
  }

  // 아이디 유효성 검사: 최소 4자리, 최대 12자리, 영문자와 숫자만 허용
  function validateUsername(username: string): boolean {
    const usernamePattern = /^[a-zA-Z0-9]{4,20}$/; // 최소 4자리, 최대 12자리
    return usernamePattern.test(username);
  }

  // 비밀번호 유효성 검사: 최소 8자리, 최대 30자리, 최소 1개의 특수 문자 포함
  function validatePassword(password: string): boolean {
    const passwordPattern = /^(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,30}$/; // 최소 8자리, 최대 30자리
    return passwordPattern.test(password);
  }

  // 인증번호 타이머 시작 (5분)
  function startTimer() {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    const initialCountdown = 300; // 5분
    setTimer(initialCountdown);

    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer === null || prevTimer <= 1) {
          clearInterval(interval);
          timerIntervalRef.current = null;
          onError("인증 시간이 만료되었습니다. 인증번호 재전송을 시도해주세요.");
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    timerIntervalRef.current = interval;
  }

  // 1단계: 정보 입력 후 인증번호 발송 요청
  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loggingIn || inFlightRef.current) return false;

    const elements = event.currentTarget.elements;
    const username = (elements.namedItem("username") as HTMLInputElement).value;
    const email = (elements.namedItem("email") as HTMLInputElement).value;
    const password = (elements.namedItem("password") as HTMLInputElement).value;
    const password2 = (elements.namedItem("password2") as HTMLInputElement).value;

    if (!username || !email || !password || !password2) return false;

    if (!validateUsername(username)) {
      setInvalidUsername(true);
      return false;
    }

    if (!validatePassword(password)) {
      setInvalidPassword(true);
      return false;
    }

    if (password !== password2) {
      setPasswordMismatch(true);
      return false;
    }

    if (!validateEmail(email)) {
      setInvalidEmail(true);
      return false;
    }

    if (!isAllowedEmailDomain(email)) {
      setInvalidEmail(true);
      onError("정식 이메일(네이버·지메일·다음·카카오·네이트·아웃룩·아이클라우드 등)만 가입할 수 있습니다. 임시/일회용 메일은 사용할 수 없습니다.");
      return false;
    }

    // 클라이언트 측 재전송 쿨다운 확인 (연속 클릭/전송 방지)
    const remainingCooldown = checkCooldown(email);
    if (remainingCooldown > 0) {
      onError(`인증 메일을 이미 전송했습니다. 약 ${remainingCooldown}초 후에 다시 시도할 수 있습니다.`);
      return false;
    }

    inFlightRef.current = true;
    setLoggingIn(true);
    setInvalidUsername(false); // 아이디 유효성 검사 통과
    setInvalidPassword(false); // 비밀번호 유효성 검사 통과
    setPasswordMismatch(false); // 비밀번호가 일치하는 경우 초기화
    setInvalidEmail(false); // 이메일이 유효한 경우 초기화

    try {
      // 회원가입 이메일 인증번호 발송 요청
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sendRegisterAuthCode`, {
        method: "POST",
        body: JSON.stringify({ username, email, hwid: window.hardware }),
        credentials: "include",
        signal: abortController.current.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      let data: any = {};
      try {
        data = await response.json();
      } catch (jsonError) {
        // JSON 파싱 실패
      }

      if (!response.ok || !data.success) {
        // 서버 발송 제한(30분에 1회)에 걸린 경우
        if (response.status === 429) {
          const mins = data.retryAfter ? Math.ceil(data.retryAfter / 60) : 30;
          startCooldown(email); // 클라이언트 쿨다운도 시작
          onError(`인증 메일은 30분에 한 번만 전송할 수 있습니다. 약 ${mins}분 후에 다시 시도해주세요.`);
        } else {
          onError(data.msg || `인증번호 발송 중 오류가 발생했습니다. (${response.status})`);
        }
        return false;
      }

      // 발송 성공: 쿨다운 시작 후 입력값 보관, 인증번호 입력 단계로 이동
      startCooldown(email);
      setAccount({ username, email, password, password2 });
      setCode("");
      setStep("code");
      startTimer();
      onError(""); // 이전 오류 메시지 제거 (발송 안내는 인증 단계 화면에 표시됨)
    } catch (err) {
      if (!(err instanceof DOMException) || err.name !== "AbortError") {
        onError("UI_TEXT_LOGIN_FAIL_UNABLE_TO_CONNECT");
        return false;
      }
      return true;
    } finally {
      inFlightRef.current = false;
      setLoggingIn(false);
      if (turnstileInput.current) turnstileInput.current.reset();
    }
  }

  // 2단계: 인증번호 확인 및 회원가입 완료
  async function handleCodeSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loggingIn) return;

    if (!code.trim()) {
      onError("인증번호를 입력하세요.");
      return;
    }
    if (timer !== null && timer <= 0) {
      onError("인증 시간이 만료되었습니다. 인증번호 재전송을 시도해주세요.");
      return;
    }

    setLoggingIn(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/account/register`, {
        method: "POST",
        body: JSON.stringify({
          username: account.username,
          email: account.email,
          password: account.password,
          password2: account.password2,
          authCode: code.trim(),
          hwid: window.hardware, // 가입 시점 HWID 기록용 (로그인과 동일 소스)
        }),
        credentials: "include",
        signal: abortController.current.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!data.success) {
        switch (data.error_code) {
          case "INVALID_EMAIL_CODE": {
            // 인증번호 불일치/만료 - 인증번호 입력 단계 유지
            onError("인증번호가 올바르지 않거나 만료되었습니다.");
            break;
          }
          case "EMAIL_VERIFICATION_REQUIRED": {
            onError("이메일 인증번호가 필요합니다.");
            break;
          }
          case "INVALID_USERNAME":
          case "INVALID_EMAIL": {
            // 1단계로 돌아가 다시 입력하도록 안내
            onError(data.result_message);
            setStep("form");
            break;
          }
          default: {
            onError(data.result_message || "회원가입 중 오류가 발생했습니다.");
          }
        }
        return;
      }

      // 회원가입 성공
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        setTimer(null);
      }
      onError("");
      setRegisterCompleted(true);
    } catch (err) {
      if (!(err instanceof DOMException) || err.name !== "AbortError") {
        onError("UI_TEXT_LOGIN_FAIL_UNABLE_TO_CONNECT");
      }
    } finally {
      setLoggingIn(false);
    }
  }

  // 인증번호 재전송
  async function handleResend() {
    if (loggingIn || inFlightRef.current) return;

    // 클라이언트 재전송 쿨다운 확인
    const remainingCooldown = checkCooldown(account.email);
    if (remainingCooldown > 0) {
      onError(`인증 메일을 이미 전송했습니다. 약 ${remainingCooldown}초 후에 다시 시도할 수 있습니다.`);
      return;
    }

    inFlightRef.current = true;
    setLoggingIn(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sendRegisterAuthCode`, {
        method: "POST",
        body: JSON.stringify({ username: account.username, email: account.email, hwid: window.hardware }),
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      let data: any = {};
      try {
        data = await response.json();
      } catch (jsonError) {
        // JSON 파싱 실패
      }

      if (!response.ok || !data.success) {
        if (response.status === 429) {
          const mins = data.retryAfter ? Math.ceil(data.retryAfter / 60) : 30;
          startCooldown(account.email);
          onError(`인증 메일은 30분에 한 번만 전송할 수 있습니다. 약 ${mins}분 후에 다시 시도해주세요.`);
        } else {
          onError(data.msg || `인증번호 재전송 중 오류가 발생했습니다. (${response.status})`);
        }
        return;
      }

      startCooldown(account.email);
      setCode("");
      startTimer();
      onError("인증번호를 다시 발송했습니다. 이메일을 확인해주세요.");
    } catch (error) {
      onError("인증번호 재전송 중 알 수 없는 오류가 발생했습니다.");
    } finally {
      inFlightRef.current = false;
      setLoggingIn(false);
    }
  }

  // 이메일 수정: 정보 입력 단계로 복귀
  function handleBackToForm() {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setTimer(null);
    setCode("");
    setStep("form");
    onError("");
  }

  function onInputChange() {
    if (!usernameInput.current || !emailInput.current || !passwordInput.current || !passwordInput2.current || !submitButton.current) return;

    const isInvalidUsername = !validateUsername(usernameInput.current.value);
    const isInvalidPassword = !validatePassword(passwordInput.current.value);
    const isMismatch = passwordInput.current.value !== passwordInput2.current.value;
    const isInvalidEmail = !validateEmail(emailInput.current.value) || !isAllowedEmailDomain(emailInput.current.value);

    setInvalidUsername(isInvalidUsername);
    setInvalidPassword(isInvalidPassword);
    setPasswordMismatch(isMismatch);
    setInvalidEmail(isInvalidEmail);

    submitButton.current.disabled = !usernameInput.current.value || !emailInput.current.value || !passwordInput.current.value || !passwordInput2.current.value || isMismatch || isInvalidEmail || isInvalidUsername || isInvalidPassword;
  }

  function abortRegister() {
    abortController.current.abort();
    setLoggingIn(false);
    handleDismiss();
  }

  if (registerCompleted) {
    return (
      <ModalWrapper handleDismiss={loggingIn ? abortRegister : handleDismiss} className="Register-Window">
        <section className="Register-Success-Wrapper" style={{ height: "400px" }}>
          <h2 className="Register-Success-Title">회원가입이 완료되었습니다.</h2>
          <AnimatedCheckmark />
          <p className="Register-Success">가입이 성공적으로 이루어졌습니다. 로그인 후 바로 테라에 접속하실 수 있습니다.</p>
          <p style={{ textAlign: "center", fontSize: "16px" }}>
            <div className="login-button">
              <br />
              <a onClick={openLoginWindow}>로그인 창으로 돌아가기</a>
            </div>
          </p>
        </section>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper handleDismiss={loggingIn ? abortRegister : handleDismiss} className="Register-Window">
      <div className="Register-Wrapper">
        <div className="Register-Title">
          <img src={GameLogo} alt="Logo" />
        </div>
        <div className="Register-Content">
          {step === "form" ? (
            <form onSubmit={handleSubmit}>
              <input type="text" name="username" className={`Form-Input ${invalidUsername ? "error" : ""}`} placeholder="아이디" disabled={loggingIn} ref={usernameInput} onChange={onInputChange} />
              {invalidUsername && <div className="error-text">아이디는 4~20자리, 영어와 숫자만 가능합니다.</div>}

              <input type="text" name="email" className={`Form-Input ${invalidEmail ? "error" : ""}`} placeholder="이메일 (네이버·지메일·다음·아웃룩 등)" disabled={loggingIn} ref={emailInput} onChange={onInputChange} />
              {invalidEmail && <div className="error-text">정식 이메일(네이버·지메일·다음·카카오·네이트·아웃룩·아이클라우드 등)만 가입할 수 있습니다.</div>}

              <input type="password" name="password" className={`Form-Input ${invalidPassword ? "error" : ""}`} placeholder="비밀번호" disabled={loggingIn} ref={passwordInput} onChange={onInputChange} />
              {invalidPassword && <div className="error-text">비밀번호는 8~30자리, 특수문자를 포함해야 합니다.</div>}

              <input type="password" name="password2" className={`Form-Input ${passwordMismatch ? "error" : ""}`} placeholder="비밀번호 확인" disabled={loggingIn} ref={passwordInput2} onChange={onInputChange} />
              {passwordMismatch && <div className="error-text">비밀번호가 일치하지 않습니다.</div>}

              <button type="submit" className="Form-Submit" ref={submitButton} disabled>
                {loggingIn ? "인증번호 발송 중..." : cooldown > 0 ? `${cooldown}초 후 재시도` : "이메일 인증번호 받기"}
              </button>

              <p style={{ textAlign: "center", fontSize: "12px", color: "#888", margin: "0 0 12px" }}>{MAIL_LIMIT_NOTICE}</p>

              <p style={{ textAlign: "center", fontSize: "14px", marginTop: "0", marginBottom: "20px" }}>
                <div className="login-button">
                  계정이 이미 있습니까?
                  <a onClick={openLoginWindow}> 로그인</a>
                </div>
              </p>
            </form>
          ) : (
            <form onSubmit={handleCodeSubmit}>
              <p style={{ textAlign: "center", fontSize: "14px", marginTop: "0", marginBottom: "16px" }}>
                <span style={{ fontWeight: "bold", color: "black" }}>{account.email}</span> 로<br />
                인증번호를 발송했습니다. 메일을 확인해주세요.
              </p>

              <input
                type="text"
                name="code"
                className="Form-Input"
                placeholder="인증번호 6자리 입력"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={loggingIn || (timer !== null && timer <= 0)}
              />

              <button type="submit" className="Form-Submit" disabled={loggingIn || !code.trim() || (timer !== null && timer <= 0)}>
                {loggingIn ? "확인 중..." : timer !== null && timer > 0 ? `${Math.floor(timer / 60)}:${timer % 60 < 10 ? "0" : ""}${timer % 60} 남음` : timer !== null && timer <= 0 ? "시간 만료" : "인증하고 가입 완료"}
              </button>

              <p style={{ textAlign: "center", fontSize: "12px", color: "#888", margin: "0 0 8px" }}>{MAIL_LIMIT_NOTICE}</p>

              <p style={{ textAlign: "center", fontSize: "14px", marginTop: "0", marginBottom: "20px" }}>
                <div className="login-button">
                  <a
                    onClick={loggingIn || cooldown > 0 ? undefined : handleResend}
                    style={{ cursor: loggingIn || cooldown > 0 ? "default" : "pointer", opacity: cooldown > 0 ? 0.5 : 1 }}
                  >
                    {cooldown > 0 ? `인증번호 재전송 (${cooldown}초)` : "인증번호 재전송"}
                  </a>
                  {" · "}
                  <a onClick={loggingIn ? undefined : handleBackToForm} style={{ cursor: loggingIn ? "default" : "pointer" }}>
                    이메일 수정
                  </a>
                </div>
              </p>
            </form>
          )}
        </div>
      </div>
      {loggingIn && <LoadingIcon dim />}
    </ModalWrapper>
  );
}
